import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
    FieldControlService,
    IField,
    ButtonComponent,
    InputTextComponent,
} from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { formatDate } from '../../utils/date-format';
import { ResponsiveService } from '../../services/responsive.service';
import { ContactValidationService } from '../../services/contact-validation.service';
import { distinctUntilChanged, filter, Subscription, takeUntil, Subject } from 'rxjs';

@Component({
    selector: 'app-popup-message',
    templateUrl: './popup-message.component.html',
    styleUrl: './popup-message.component.scss',
    providers: [FieldControlService],
    standalone: true,
    imports: [NgClass, NgStyle, ReactiveFormsModule, ButtonComponent, InputTextComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopupMessageComponent implements OnInit, OnDestroy {
    popupDisplayObj: any;
    mobileForm!: UntypedFormGroup;
    desktopForm!: UntypedFormGroup;
    readonly isMobile = signal(false);
    readonly isVerifyingEmail = signal(false);
    sink = new Subscription();
    private destroy$ = new Subject<void>();

    popupCallbackDetail: string[] = [];
    isEmailVerified!: boolean;

    private readonly contactValidationService = inject(ContactValidationService);

    constructor(
        public bsModalRef: BsModalRef,
        private responsiveService: ResponsiveService,
        private fieldService: FieldControlService,
        private cdr: ChangeDetectorRef
    ) {
        this.isMobile.set(this.responsiveService.isMobile);
        this.sink.add(
            this.responsiveService.isMobile$.subscribe((isMobile) => {
                this.isMobile.set(isMobile);
            })
        );
    }

    ngOnInit(): void {
        this.mobileForm = this.fieldService.toFormGroup(this.popupDisplayObj.fields as IField[]);
        this.desktopForm = this.fieldService.toFormGroup(this.popupDisplayObj.fields as IField[]);
        this.isEmailVerified = this.popupDisplayObj.emailVerified === false ? false : true;

        this.sink.add(
            this.desktopForm
                .valueChanges.pipe(
                    distinctUntilChanged(),
                    filter(() => this.desktopForm.valid)
                )
                .subscribe(() => {
                    setTimeout(() => {
                        this.isEmailVerified = false;
                        if (!this.isMobile() && this.desktopForm.valid && this.desktopForm.controls['uploadDoc']) {
                            this.isEmailVerified = this.desktopForm.controls['uploadDoc'].value ? true : false;
                        }
                    });
                })
        );

        this.sink.add(
            this.mobileForm
                .valueChanges.pipe(
                    distinctUntilChanged(),
                    filter(() => this.mobileForm.valid)
                )
                .subscribe(() => {
                    setTimeout(() => {
                        this.isEmailVerified = false;
                        if (this.isMobile() && this.mobileForm.valid && this.mobileForm.controls['uploadDoc']) {
                            this.isEmailVerified = this.mobileForm.controls['uploadDoc'].value ? true : false;
                        }
                    });
                })
        );
        if (document.getElementsByClassName('cdk-overlay-container')) {
            let elements = document.getElementsByClassName('cdk-overlay-container');
            for (let i = 0; i < elements.length; i++) {
                elements[i].setAttribute('style', 'visibility: hidden;');
            }
        }
        this.cdr.detectChanges();
    }

    formatValue(value: string): string {
        return formatDate(value, 'MM/DD/YYYY');
    }

    hasEmailField(): boolean {
        return this.popupDisplayObj?.fields?.some((field: any) => field.type === 'email') || false;
    }

    verifyEmail() {
        const email = this.isMobile()
            ? this.mobileForm.controls['email']?.value
            : this.desktopForm.controls['email']?.value;

        if (!email) {
            this.isEmailVerified = false;
            return;
        }

        this.isVerifyingEmail.set(true);

        this.contactValidationService
            .validateEmailInSystem(email)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.isVerifyingEmail.set(false);
                    const emailControl = this.isMobile()
                        ? this.mobileForm.controls['email']
                        : this.desktopForm.controls['email'];

                    if (response?.statusCode === 200 && response?.body?.validateContactResponse?.length > 0) {
                        const contactResponse = response.body.validateContactResponse[0];
                        const isValid = contactResponse?.isvalid === 'true' || contactResponse?.isvalid === true;
                        emailControl?.setErrors(isValid ? null : { email: true });
                        this.isEmailVerified = isValid;
                    } else {
                        emailControl?.setErrors({ email: true });
                        this.isEmailVerified = false;
                    }
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.isVerifyingEmail.set(false);
                    const emailControl = this.isMobile()
                        ? this.mobileForm.controls['email']
                        : this.desktopForm.controls['email'];
                    emailControl?.setErrors({ email: true });
                    this.isEmailVerified = false;
                    this.cdr.detectChanges();
                },
            });
    }

    footerButtonAction(event: any) {
        this.popupCallbackDetail.push(event);
        if (this.isMobile() && this.mobileForm.valid && this.mobileForm.controls['email']) {
            this.popupCallbackDetail.push(this.mobileForm.controls['email'].value);
        }
        if (!this.isMobile() && this.desktopForm.valid && this.desktopForm.controls['email']) {
            this.popupCallbackDetail.push(this.desktopForm.controls['email'].value);
        }
        this.bsModalRef.hide();

        // Force immediate modal removal without animation
        setTimeout(() => {
          const backdrop = document.querySelector('.modal-backdrop');
          const modal = document.querySelector('.modal');
          if (backdrop) backdrop.remove();
          if (modal) modal.remove();
        }, 0);

        if (document.getElementsByClassName('cdk-overlay-container')) {
            let elements = document.getElementsByClassName('cdk-overlay-container');
            for (let i = 0; i < elements.length; i++) {
                elements[i].setAttribute('style', 'visibility: visible;');
            }
        }
    }

    formButtonAction(event: any) {
        this.popupCallbackDetail.push(event);
        if (this.isMobile() && this.mobileForm.valid && this.mobileForm.controls['email']) {
            this.popupCallbackDetail.push(this.mobileForm.controls['email'].value);
        }
        if (!this.isMobile() && this.desktopForm.valid && this.desktopForm.controls['email']) {
            this.popupCallbackDetail.push(this.desktopForm.controls['email'].value);
        }
        this.bsModalRef.hide();

        // Force immediate modal removal without animation
        setTimeout(() => {
          const backdrop = document.querySelector('.modal-backdrop');
          const modal = document.querySelector('.modal');
          if (backdrop) backdrop.remove();
          if (modal) modal.remove();
        }, 0);

        if (document.getElementsByClassName('cdk-overlay-container')) {
            let elements = document.getElementsByClassName('cdk-overlay-container');
            for (let i = 0; i < elements.length; i++) {
                elements[i].setAttribute('style', 'visibility: visible;');
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.sink.unsubscribe();
    }
}
